
import { Component, Vue } from 'vue-property-decorator';
import CustomerRepository from '@/api/repositories/CustomerRepository';
import StatisticsRepository from '@/api/repositories/StatisticsRepository';
import RideRepository from '@/api/repositories/RideRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import Ride from '@/models/Ride';
import debounce, { DebouncedFunction } from '@/misc/debounce';

const customerRepository: CustomerRepository = RepositoryFactory.get('customer');
const statisticsRepository: StatisticsRepository = RepositoryFactory.get('statistics');
const rideRepository: RideRepository = RepositoryFactory.get('ride');

/**
 * Same numbers as the driver/taxi search (SearchDriver.view.vue): the search
 * hits the server, so typing has to settle before it fires on its own.
 */
const SEARCH_DEBOUNCE_MS = 400;
const MIN_AUTO_SEARCH_LENGTH = 3;

/** The backend rejects shorter queries with a 422, so the button does too. */
const MIN_SEARCH_LENGTH = 2;

enum CustomerTab {
    SEARCH = 0,
    TOP = 1
}

/**
 * One normalised row for both tables, whichever endpoint it came from. The
 * ride history opens off it, so it has to carry the id.
 */
interface CustomerRow {
    id: string,
    name: string,
    phone: string,
    status: 'ACTIVE' | 'BLOCKED' | 'DELETED' | 'UNVERIFIED'
}

@Component({
    components: {
        RideTableComponent: () => import(
            /* webpackChunkName: "RideTableComponent" */
            '@/components/RideTable.component.vue')
    }
})
export default class CustomerLookupView extends Vue {
    public activeTab: number = CustomerTab.SEARCH;

    /* search tab */
    public query: string = '';
    public customerRows: any[] = [];
    public isLoading: boolean = false;

    /**
     * Monotonic counters identifying the most recent request of each kind, so
     * a slower response that comes back out of order is dropped rather than
     * overwriting a newer one (the F2a search pattern).
     */
    private customerSearchToken: number = 0;
    private topToken: number = 0;
    private historyToken: number = 0;

    private debouncedCustomerSearch: DebouncedFunction = debounce(() => this.searchCustomers(true), SEARCH_DEBOUNCE_MS);

    /* top customers tab */
    public selectedWindow: string = '7d';
    public topOrder: string = 'desc';
    public topLimit: number = 100;
    public limitOptions: number[] = [25, 50, 100, 500];
    public topRows: any[] = [];
    public isLoadingTop: boolean = false;
    private topLoaded: boolean = false;

    /* ride history */
    public historyDialog: boolean = false;
    public historyCustomer: CustomerRow | null = null;
    public historyRides: Ride[] = [];
    public isLoadingHistory: boolean = false;

    public customerHeaders = [
        { text: this.$t('CUSTOMERS.TABLE.NAME'), value: 'name', sortable: true },
        { text: this.$t('CUSTOMERS.TABLE.PHONE'), value: 'phone', sortable: true },
        { text: this.$t('CUSTOMERS.TABLE.STATUS'), value: 'status', sortable: true },
        { text: this.$t('CUSTOMERS.TABLE.REGISTERED'), value: 'createdAt', sortable: true },
        { text: this.$t('CUSTOMERS.TABLE.LAST_LOGIN'), value: 'lastLogin', sortable: true }
    ];

    public topHeaders = [
        { text: this.$t('CUSTOMERS.TABLE.RANK'), value: 'rank', sortable: false, width: 60 },
        { text: this.$t('CUSTOMERS.TABLE.NAME'), value: 'name', sortable: false },
        { text: this.$t('CUSTOMERS.TABLE.PHONE'), value: 'phone', sortable: false },
        { text: this.$t('CUSTOMERS.TABLE.STATUS'), value: 'status', sortable: false },
        { text: this.$t('CUSTOMERS.TABLE.TRIPS'), value: 'trips', sortable: false },
        { text: this.$t('CUSTOMERS.TABLE.LAST_TRIP'), value: 'lastTrip', sortable: false }
    ];

    /** The live-counts windows plus the two longer ones the ranking endpoint adds. */
    public get windowOptions() {
        return [
            { value: 'today', text: this.$t('CUSTOMERS.TOP.WINDOWS.TODAY').toString() },
            { value: '12h', text: this.$t('CUSTOMERS.TOP.WINDOWS.H12').toString() },
            { value: '24h', text: this.$t('CUSTOMERS.TOP.WINDOWS.H24').toString() },
            { value: '3d', text: this.$t('CUSTOMERS.TOP.WINDOWS.D3').toString() },
            { value: '7d', text: this.$t('CUSTOMERS.TOP.WINDOWS.D7').toString() },
            { value: '30d', text: this.$t('CUSTOMERS.TOP.WINDOWS.D30').toString() },
            { value: 'all', text: this.$t('CUSTOMERS.TOP.WINDOWS.ALL').toString() }
        ];
    }

    public async onTabChanged(tab: number) {
        if (tab === CustomerTab.TOP && !this.topLoaded) {
            await this.loadTopCustomers();
        }
    }

    /**
     * Runs on every keystroke. Below the minimum length there is nothing worth
     * asking the server, so drop any pending search and clear the table —
     * bumping the token first so a slower in-flight request cannot refill it.
     */
    public onCustomerQueryInput() {
        if (!this.isAutoSearchableQuery) {
            this.debouncedCustomerSearch.cancel();
            this.customerSearchToken++;
            this.customerRows = [];
            this.isLoading = false;
            return;
        }
        this.debouncedCustomerSearch();
    }

    /**
     * @param auto whether typing triggered this rather than the button. Errors
     * are swallowed when it did: half-typed queries fail routinely and would
     * otherwise stack up error toasts as the user types.
     *
     * Template call sites must keep the explicit `searchCustomers()` parentheses:
     * a bare `@click="searchCustomers"` would hand the click event in as `auto`.
     */
    public async searchCustomers(auto: boolean = false) {
        if (!this.isValidQuery) return;

        const token = ++this.customerSearchToken;
        this.isLoading = true;
        try {
            const res = await customerRepository.search(this.query.trim());
            if (token !== this.customerSearchToken) return;
            this.customerRows = (res.data ?? []).map((customer: any) => this.normalizeCustomer(customer));
        } catch (e) {
            if (token !== this.customerSearchToken) return;
            if (!auto) this.$notifyErrorSimplified('CUSTOMERS.SEARCH.ERROR');
            this.customerRows = [];
        } finally {
            if (token === this.customerSearchToken) this.isLoading = false;
        }
    }

    public get isValidQuery(): boolean {
        return this.query.trim().length >= MIN_SEARCH_LENGTH;
    }

    public get isAutoSearchableQuery(): boolean {
        return this.query.trim().length >= MIN_AUTO_SEARCH_LENGTH;
    }

    public async toggleTopOrder() {
        this.topOrder = this.topOrder === 'desc' ? 'asc' : 'desc';
        await this.loadTopCustomers();
    }

    /**
     * Token-guarded like the live-counts screen: switching the window while a
     * request is still in flight must not let the older answer land last.
     */
    public async loadTopCustomers() {
        const token = ++this.topToken;
        this.isLoadingTop = true;
        try {
            const res = await statisticsRepository.getCustomerTrips(this.selectedWindow, this.topOrder, this.topLimit);
            if (token !== this.topToken) return;
            this.topRows = (res.data ?? []).map((row: any, index: number) => ({
                rank: index + 1,
                ...this.normalizeCustomer(row.customer ?? {}),
                trips: row.trips ?? 0,
                lastTrip: row.lastTrip ?? null
            }));
            this.topLoaded = true;
        } catch (e) {
            if (token !== this.topToken) return;
            this.$notifyErrorSimplified('CUSTOMERS.TOP.ERROR');
            this.topRows = [];
        } finally {
            if (token === this.topToken) this.isLoadingTop = false;
        }
    }

    public async openHistory(row: CustomerRow) {
        if (!row || !row.id) return;

        this.historyCustomer = row;
        this.historyRides = [];
        this.historyDialog = true;

        const token = ++this.historyToken;
        this.isLoadingHistory = true;
        try {
            const res = await rideRepository.customerHistory(row.id);
            if (token !== this.historyToken) return;
            this.historyRides = Ride.parseFromArray(res.data ?? []) as Ride[];
        } catch (e) {
            if (token !== this.historyToken) return;
            this.$notifyErrorSimplified('CUSTOMERS.HISTORY.ERROR');
            this.historyRides = [];
        } finally {
            if (token === this.historyToken) this.isLoadingHistory = false;
        }
    }

    public closeHistory() {
        // Bump the token so a history still loading cannot pop into the next
        // customer's dialog.
        this.historyToken++;
        this.historyDialog = false;
        this.isLoadingHistory = false;
    }

    public get historySummary() {
        const status = (ride: Ride) => ride.offer?.status ?? '';
        return {
            total: this.historyRides.length,
            completed: this.historyRides.filter((r: Ride) => status(r) === 'CLOSED').length,
            cancelled: this.historyRides.filter((r: Ride) => status(r).startsWith('CANCELED')).length
        };
    }

    public statusColor(status: CustomerRow['status']): string {
        switch (status) {
            case 'BLOCKED': return 'error';
            case 'DELETED': return 'grey';
            case 'UNVERIFIED': return 'warning';
            default: return 'success';
        }
    }

    /**
     * Accepts both the raw customer document (search) and the ranking's
     * `customer` sub-object, which already carries a joined `name`. Nearly
     * every customer on the platform is a phone-only record, so the phone is
     * what actually identifies a row and the name is often a dash.
     */
    private normalizeCustomer(customer: any) {
        const name = customer.name ?? `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim();
        const phone = customer.phone?.phoneNumber
            ? `${customer.phone.prefix ?? ''} ${customer.phone.phoneNumber}`.trim()
            : '-';
        let status: CustomerRow['status'] = 'ACTIVE';
        if (customer.isDeleted) {
            status = 'DELETED';
        } else if (customer.isBlocked) {
            status = 'BLOCKED';
        } else if (customer.isVerified === false) {
            status = 'UNVERIFIED';
        }
        return {
            id: customer.id ?? customer._id ?? '',
            name: name || '-',
            phone,
            status,
            createdAt: customer.createdAt ?? null,
            lastLogin: customer.lastLogin ?? null
        };
    }
}
